/**
 * Firm Analytics (owners and admins): the firm on six pages — Overview, Revenue, Billing &
 * Collections, Staff & Capacity, Clients and Growth. Every figure is compared with the same dates
 * last year; changes worked out from fewer than five items show the two counts instead of a
 * percentage; most figures open the list behind them.
 */
import {
  ANALYTICS_PAGES,
  ANALYTICS_PAGE_LABELS,
  PERIODS,
  PERIOD_LABELS,
  SMALL_NUMBER,
  compareWithLastYear,
  formatCents,
  formatMinutes,
  type AnalyticsPage as Page,
  type PeriodKind,
} from "@clientflow/shared";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, ChevronRight, Info, Minus, Sparkles } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, type TooltipProps } from "recharts";
import { Link, useLocation, useSearch } from "wouter";
import { Empty, Loading, PageHeader, Pager, Section, Segmented, usePaged, useServerPaged } from "@/components/page";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/format";
import type { Me } from "@/lib/session";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

// --- Response shapes -------------------------------------------------------------------------------

type Period = { kind: PeriodKind; label: string; current: { from: string; to: string }; previous: { from: string; to: string } };
type Compared = { current: number; previous: number; count: number; prevCount: number };
type Common = { period: Period; today: string; timeTracking: boolean; owner: boolean };
type AgingBucket = { key: string; label: string; cents: number; invoices: number; clientCount: number; clients: Array<{ clientId: string; name: string; cents: number }> };

type Overview = Common & {
  billed: Compared;
  collected: Compared;
  receivables: { totalCents: number; over60Cents: number; over60Clients: number; invoices: number };
  recurring: { monthlyCents: number; plans: number; plansWithoutFee: number };
  clients: { active: number; gained: { current: number; previous: number }; lost: { current: number; previous: number } };
  work: { open: number; late: number; byService: Array<{ module: string; label: string; open: number; late: number; stages: Array<{ key: string; name: string; count: number; late: number }> }> };
  attention: Array<{ tone: "danger" | "warning" | "info"; text: string; href: string }>;
  trend: Array<{ month: string; receivablesCents: number; recurringCents: number; activeClients: number; openEngagements: number }>;
};
type Revenue = Common & {
  months: Array<{ month: string; billed: number; collected: number; billedLastYear: number; collectedLastYear: number }>;
  byService: Array<{ key: string; label: string } & Compared>;
  total: { current: number; previous: number };
  recurringVsOneTime: { recurring: { current: number; previous: number }; oneTime: { current: number; previous: number }; monthlyPlans: Overview["recurring"] };
  topClients: Array<{ clientId: string; name: string; cents: number; invoices: number; share: number }>;
  clientsBilled: { current: number; previous: number };
  perClient: Compared;
  byReturnType: Array<{ returnType: string; label: string; returns: number; cents: number; averageCents: number; prevReturns: number; prevAverageCents: number }>;
  quotedVsBilled: { engagements: number; quotedCents: number; billedCents: number; under: number; items: Array<{ engagementId: string; name: string; client: string; quotedCents: number; billedCents: number }> };
};
type Billing = Common & {
  aging: AgingBucket[];
  daysToPay: { current: number | null; previous: number | null; count: number; prevCount: number };
  collectionRate: { current: number | null; previous: number | null; billed: Compared; collected: Compared };
  methods: Array<{ method: string; payments: number; cents: number; averageDays: number | null }>;
  held: { documents: number; invoices: number; cents: number };
  dueSoon: { next7: { cents: number; invoices: number }; next30: { cents: number; invoices: number } };
  fromRefunds?: { awaitingApproval: { invoices: number; cents: number }; declined: number; awaitingTransfer: { invoices: number; cents: number }; received: { payments: number; cents: number } };
  unbilled: { count: number; quotedCents: number; withoutQuote: number; items: Array<{ engagementId: string; name: string; service: string; clientId: string; client: string; quotedCents: number | null; completedOn: string }> };
};
type Person = {
  userId: string;
  name: string;
  open: { total: number; late: number; dueNext14: number; byService: Array<{ module: string; label: string; count: number }> };
  onTime: { engagements: number; engagementsOnTime: number; tasks: number; tasksOnTime: number };
  returnsCompleted: Array<{ returnType: string | null; label: string; count: number }>;
  review: { signedOff: number; medianDays: number | null } | null;
  billedCents: number;
  time: { minutes: number; label: string; capacityMinutes: number; utilization: number | null; costCents?: number | null; hourlyCostCents?: number | null; weeklyHours?: number } | null;
};
type Staff = Common & {
  trackerOn: boolean;
  showsCost: boolean;
  people: Person[];
  stageTimes: Array<{ module: string; label: string; stages: Array<{ key: string; name: string; engagements: number; averageDays: number }> }>;
  clientResponse: { current: { requests: number; medianDays: number | null; averageReminders: number | null }; previous: { requests: number; medianDays: number | null; averageReminders: number | null } };
};
type Clients = Common & {
  active: number;
  gained: { current: number; previous: number };
  lost: { current: number; previous: number };
  retention: { current: number | null; previous: number | null; count: number; prevCount: number };
  returningTaxClients: { current: { taxYear: number; lastYear: number; back: number }; previous: { taxYear: number; lastYear: number; back: number } };
  byService: Array<{ module: string; label: string; clients: number }>;
  multiService: number;
  opportunities: Array<{ label: string; offer: string; service: string; count: number; clients: Array<{ clientId: string; name: string }> }>;
  atRisk: { count: number; items: Array<{ clientId: string; name: string; reasons: string[] }> };
  onboarding: { clients: number; leadToClientDays: number | null; clientToSignedDays: number | null; clientToInvoiceDays: number | null };
  profitability: null | { items: Array<{ clientId: string; name: string; minutes: number; billedCents: number; costCents: number | null }>; missingRates: number };
};
type Growth = Common & {
  leads: { current: number; previous: number; won: number; prevWon: number; sources: Array<{ source: string; label: string; leads: number; won: number; medianHoursToContact: number | null; revenueCents: number; prevLeads: number; prevWon: number }> };
  meetings: { current: Meetings; previous: Meetings };
  ai: { current: AiSaved; previous: AiSaved; assumptions: { read: number; proposal: number } };
};
type Meetings = { booked: number; held: number; noShows: number; cancelled: number; consultations: number; consultationsWon: number };
type AiSaved = { documentsRead: number; proposalsApproved: number; minutesSaved: number };
type ListItem = { id: string; href: string; title: string; subtitle: string; date: string; cents: number | null; status: string };
type Drill = { kind: string; title: string; params?: Record<string, string> };

// --- Formatting ------------------------------------------------------------------------------------

const money = (c: number | null | undefined) => (c === null || c === undefined ? "—" : formatCents(Math.round(c), { showCents: false }));
const pct = (v: number | null | undefined, digits = 0) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(digits)}%`);
const days = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v < 10 ? v.toFixed(1) : Math.round(v)} day${Math.round(v * 10) === 10 ? "" : "s"}`);
const monthLabel = (m: string) => new Date(`${m}-15T12:00:00`).toLocaleDateString("en-US", { month: "short" });
const monthLong = (m: string) => new Date(`${m}-15T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
function compact(v: number): string {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `$${(a / 1_000).toFixed(a >= 10_000 ? 0 : 1)}k`;
  return `$${a.toFixed(0)}`;
}
const AXIS = { stroke: "var(--viz-axis)", tick: { fill: "var(--viz-ink)", fontSize: 11 }, tickLine: false } as const;
const LEGEND = { iconType: "circle", iconSize: 8, wrapperStyle: { fontSize: 12 }, formatter: (v: string) => <span style={{ color: "var(--viz-ink)" }}>{v}</span> } as const;

/** How a figure compares with the same dates last year, following the small-number rule. */
function Change({ current, previous, count, prevCount, format = (v: number) => String(v), invert = false, points = false }: { current: number | null; previous: number | null; count: number; prevCount: number; format?: (v: number) => string; invert?: boolean; points?: boolean }) {
  if (current === null || previous === null || (count === 0 && prevCount === 0)) return <span className="text-xs text-muted-foreground">Nothing to compare with last year</span>;
  if (count < SMALL_NUMBER || prevCount < SMALL_NUMBER) {
    return (
      <span className="text-xs text-muted-foreground">
        {format(current)} vs {format(previous)} last year
      </span>
    );
  }
  let delta: number | null;
  let text: string;
  if (points) {
    delta = (current - previous) * 100;
    text = `${Math.abs(delta).toFixed(1)} pts`;
  } else {
    const c = compareWithLastYear(current, previous, { current: count, previous: prevCount });
    if (c.kind !== "pct") return <span className="text-xs text-muted-foreground">{format(previous)} last year</span>;
    delta = c.pct;
    text = `${Math.abs(c.pct).toFixed(1)}%`;
  }
  const up = delta > 0.05;
  const down = delta < -0.05;
  const good = invert ? down : up;
  const bad = invert ? up : down;
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs">
      <span className={cn("inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold", good ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : bad ? "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300" : "bg-muted text-muted-foreground")}>
        <Icon className="h-3 w-3" aria-hidden />
        <span className="sr-only">{up ? "up" : down ? "down" : "unchanged"}</span>
        {text}
      </span>
      <span className="text-muted-foreground">vs {format(previous)} last year</span>
    </span>
  );
}

/** A headline figure: label, value, comparison and, when it has one, the list behind it. */
function Kpi({ label, value, children, onOpen, tone, hint }: { label: string; value: ReactNode; children?: ReactNode; onOpen?: () => void; tone?: "danger" | "warning"; hint?: string }) {
  const body = (
    <>
      <p className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground">
        <span className="truncate" title={hint}>
          {label}
        </span>
        {onOpen && <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-50 transition group-hover:translate-x-0.5 group-hover:opacity-100" aria-hidden />}
      </p>
      <p className={cn("tabular mt-1 text-xl font-semibold sm:text-[1.6rem] leading-tight tracking-tight", tone === "danger" ? "text-red-700 dark:text-red-300" : tone === "warning" ? "text-amber-700 dark:text-amber-300" : "text-foreground")}>{value}</p>
      {children && <div className="mt-1.5 min-h-[1.25rem]">{children}</div>}
    </>
  );
  const box = "group min-w-0 rounded-lg border border-card-border bg-card px-3 py-3 text-left sm:px-4 sm:py-3.5 shadow-[var(--card-shadow)]";
  return onOpen ? (
    <button type="button" onClick={onOpen} className={cn(box, "transition duration-150 hover:-translate-y-px hover:border-primary/40 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary")}>
      {body}
    </button>
  ) : (
    <div className={box}>{body}</div>
  );
}

function Kpis({ children, cols = 4 }: { children: ReactNode; cols?: 2 | 3 | 4 }) {
  return <div className={cn("mb-6 grid grid-cols-2 gap-2.5 sm:gap-3", cols === 3 ? "lg:grid-cols-3" : cols === 4 ? "lg:grid-cols-4" : "")}>{children}</div>;
}

/** A share of a total as a thin bar, with its label and value. */
function ShareBar({ label, value, share, sub, onClick, tone = "primary" }: { label: ReactNode; value: ReactNode; share: number; sub?: ReactNode; onClick?: () => void; tone?: "primary" | "warning" | "danger" | "muted" }) {
  const color = { primary: "bg-primary", warning: "bg-amber-500", danger: "bg-red-500", muted: "bg-muted-foreground/40" }[tone];
  const inner = (
    <>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate">{label}</span>
        <span className="tabular shrink-0 font-medium">{value}</span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className={cn("h-full rounded-full", color)} style={{ width: `${Math.max(0, Math.min(100, share * 100))}%` }} />
      </div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className="block w-full rounded-md px-2 py-1.5 text-left hover:bg-muted/60 focus-visible:outline-2 focus-visible:outline-primary">
      {inner}
    </button>
  ) : (
    <div className="px-2 py-1.5">{inner}</div>
  );
}

/** A list shown a page at a time. */
function PagedList<T>({ items, noun, children }: { items: readonly T[]; noun: string; children: (page: T[]) => ReactNode }) {
  const p = usePaged(items);
  return (
    <>
      {children(p.items)}
      <Pager paged={p} noun={noun} className="mt-3" compact />
    </>
  );
}

function Note({ children, tone = "info" }: { children: ReactNode; tone?: "info" | "warning" }) {
  return (
    <p className={cn("flex items-start gap-2 rounded-md border px-3 py-2 text-xs", tone === "warning" ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" : "border-border bg-muted/40 text-muted-foreground")}>
      {tone === "warning" ? <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden /> : <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />}
      <span>{children}</span>
    </p>
  );
}

function ChartTip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      <p className="mb-1 font-semibold text-foreground">{label}</p>
      {payload.map((p) => (
        <p key={String(p.dataKey)} className="flex items-center gap-2 text-muted-foreground">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="flex-1">{p.name}</span>
          <span className="tabular font-medium text-foreground">{formatCents(Math.round(Number(p.value) * 100), { showCents: false })}</span>
        </p>
      ))}
    </div>
  );
}

// --- The page ----------------------------------------------------------------------------------------

export function AnalyticsPage({ page: raw, me }: { page: string; me: Me }) {
  const page = (ANALYTICS_PAGES as readonly string[]).includes(raw) ? (raw as Page) : "overview";
  const [, navigate] = useLocation();
  const search = new URLSearchParams(useSearch());
  const period = ((PERIODS as readonly string[]).includes(search.get("period") ?? "") ? search.get("period") : "ytd") as PeriodKind;
  const year = search.get("year") ?? "";
  const qs = `period=${period}${period === "year" && year ? `&year=${year}` : ""}`;
  const { data, error, isLoading } = useQuery<Common & Record<string, unknown>>({ queryKey: [`/api/analytics/${page}?${qs}`] });
  const [drill, setDrill] = useState<Drill | null>(null);
  const go = (p: Page, q = qs) => navigate(`/analytics${p === "overview" ? "" : `/${p}`}?${q}`);
  const thisYear = Number((data?.today ?? new Date().toISOString()).slice(0, 4));
  const open = (d: Drill) => setDrill(d);
  void me;

  return (
    <>
      <PageHeader
        crumbs={[{ label: "Firm Analytics", href: "/analytics" }]}
        title={ANALYTICS_PAGE_LABELS[page]}
        // Two lines on a phone once the dates are in, so the line holds that height while they load.
        description={<span className="block min-h-10 md:min-h-0">{data ? `${data.period.label}: ${formatDate(data.period.current.from)} – ${formatDate(data.period.current.to)}, compared with the same dates last year.` : "Compared with the same dates last year."}</span>}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="an-period">
              Period
            </label>
            <select id="an-period" value={period} onChange={(e) => go(page, `period=${e.target.value}`)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              {PERIODS.map((p) => (
                <option key={p} value={p}>
                  {PERIOD_LABELS[p]}
                </option>
              ))}
            </select>
            {period === "year" && (
              <>
                <label className="sr-only" htmlFor="an-year">
                  Year
                </label>
                <select id="an-year" value={year || String(thisYear - 1)} onChange={(e) => go(page, `period=year&year=${e.target.value}`)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                  {Array.from({ length: 6 }, (_, i) => thisYear - i).map((y) => (
                    <option key={y} value={y}>
                      {y}
                      {y === thisYear ? " (to date)" : ""}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        }
      />
      <Segmented label="Analytics pages" className="mb-6" value={page} onChange={(p) => go(p)} options={ANALYTICS_PAGES.map((p) => ({ value: p, label: ANALYTICS_PAGE_LABELS[p] }))} />
      {isLoading ? (
        <Loading tall />
      ) : error || !data ? (
        <Empty>These figures couldn't be loaded. Please try again.</Empty>
      ) : page === "overview" ? (
        <OverviewView d={data as unknown as Overview} open={open} />
      ) : page === "revenue" ? (
        <RevenueView d={data as unknown as Revenue} open={open} />
      ) : page === "billing" ? (
        <BillingView d={data as unknown as Billing} open={open} />
      ) : page === "staff" ? (
        <StaffView d={data as unknown as Staff} open={open} />
      ) : page === "clients" ? (
        <ClientsView d={data as unknown as Clients} open={open} />
      ) : (
        <GrowthView d={data as unknown as Growth} />
      )}
      <p className="mt-8 text-xs leading-5 text-muted-foreground">
        Billed means invoices issued (drafts and voided invoices aren't counted). Collected means payments received on their date (refunds aren't counted). A change worked out from fewer than {SMALL_NUMBER} items shows both figures instead of a percentage. Only owners and admins see these pages, and each view is recorded in the audit log.
      </p>
      <DrillDown drill={drill} qs={qs} onClose={() => setDrill(null)} />
    </>
  );
}

/** The list behind a figure. */
function DrillDown({ drill, qs, onClose }: { drill: Drill | null; qs: string; onClose: () => void }) {
  const params = drill ? new URLSearchParams({ kind: drill.kind, ...(drill.params ?? {}) }).toString() : "";
  // The server sends a page at a time; the count and amount in the header are over the whole list.
  const pages = useServerPaged(`${qs}&${params}`);
  const base = `/api/analytics/list?${qs}&${params}`;
  const { data, isLoading } = useQuery<{ items: ListItem[]; total: number; totalCents: number }>({
    queryKey: [`${base}&offset=${pages.offset}&limit=${pages.size}`],
    enabled: !!drill,
    // Keeps the current page on screen while the next loads, but never another figure's list.
    placeholderData: (prev, prevQuery) => (String(prevQuery?.queryKey[0] ?? "").startsWith(`${base}&offset=`) ? prev : undefined),
  });
  const total = data?.totalCents ?? 0;
  return (
    <Dialog open={!!drill} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden p-0">
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle>{drill?.title}</DialogTitle>
          <DialogDescription>{data ? `${data.total.toLocaleString("en-US")} item${data.total === 1 ? "" : "s"}${total ? ` · ${money(total)}` : ""}` : " "}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto px-2 pb-3">
          {isLoading ? (
            <Loading />
          ) : !data?.items.length ? (
            <div className="p-4">
              <Empty>Nothing here.</Empty>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {data.items.map((x) => (
                <li key={x.id}>
                  <Link href={x.href} onClick={onClose} className="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-muted/60">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{x.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{x.subtitle}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      {x.cents !== null && <p className="tabular text-sm font-medium">{formatCents(Math.round(x.cents))}</p>}
                      {x.date && <p className={cn("text-xs", x.status === "late" ? "font-medium text-red-700 dark:text-red-300" : "text-muted-foreground")}>{formatDate(x.date)}</p>}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {data && <Pager paged={pages.paged(data.total)} noun="items" compact className="mx-3 mt-1" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Overview -----------------------------------------------------------------------------------------

function OverviewView({ d, open }: { d: Overview; open: (x: Drill) => void }) {
  const trend = d.trend.slice(-13);
  return (
    <>
      {d.attention.length > 0 && (
        <section aria-label="Needs your attention" className="mb-6 rounded-lg border border-card-border bg-card">
          <h2 className="flex items-center gap-2 border-b border-border px-5 py-3 text-[15px] font-semibold">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden /> Needs your attention
          </h2>
          <ul className="divide-y divide-border">
            {d.attention.map((a, i) => (
              <li key={i}>
                <Link href={a.href} className="flex items-center gap-3 px-5 py-3 text-sm hover:bg-muted/50">
                  <span className={cn("h-2 w-2 shrink-0 rounded-full", a.tone === "danger" ? "bg-red-500" : a.tone === "warning" ? "bg-amber-500" : "bg-primary/60")} aria-hidden />
                  <span className="flex-1">{a.text}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      <Kpis>
        <Kpi label="Billed" value={money(d.billed.current)} onOpen={() => open({ kind: "billed", title: "Invoices issued" })}>
          <Change {...d.billed} format={money} />
        </Kpi>
        <Kpi label="Collected" value={money(d.collected.current)} onOpen={() => open({ kind: "collected", title: "Payments received" })}>
          <Change {...d.collected} format={money} />
        </Kpi>
        <Kpi label="Clients owe you" value={money(d.receivables.totalCents)} tone={d.receivables.over60Cents > 0 ? "warning" : undefined} onOpen={() => open({ kind: "receivables", title: "Open invoices" })}>
          <span className="text-xs text-muted-foreground">{d.receivables.over60Cents > 0 ? `${money(d.receivables.over60Cents)} over 60 days late` : "Nothing over 60 days late"}</span>
        </Kpi>
        <Kpi label="Recurring monthly revenue" value={money(d.recurring.monthlyCents)} hint="The monthly fees of active accounting, payroll and CFO plans">
          <span className="text-xs text-muted-foreground">
            {d.recurring.plans} plan{d.recurring.plans === 1 ? "" : "s"}
            {d.recurring.plansWithoutFee > 0 && <span className="text-amber-700 dark:text-amber-300"> · {d.recurring.plansWithoutFee} without a fee</span>}
          </span>
        </Kpi>
        <Kpi label="Active clients" value={d.clients.active}>
          <span className="text-xs text-muted-foreground">
            <button type="button" className="hover:underline" onClick={() => open({ kind: "gained", title: "New clients" })}>
              +{d.clients.gained.current} new
            </button>{" "}
            ·{" "}
            <button type="button" className="hover:underline" onClick={() => open({ kind: "lost", title: "Clients lost" })}>
              {d.clients.lost.current} lost
            </button>{" "}
            (last year +{d.clients.gained.previous} / {d.clients.lost.previous})
          </span>
        </Kpi>
        <Kpi label="Open work" value={d.work.open} onOpen={() => open({ kind: "open_work", title: "Open engagements" })}>
          <span className="text-xs text-muted-foreground">Across {d.work.byService.length} service{d.work.byService.length === 1 ? "" : "s"}</span>
        </Kpi>
        <Kpi label="Past due" value={d.work.late} tone={d.work.late > 0 ? "danger" : undefined} onOpen={() => open({ kind: "late_work", title: "Engagements past their due date" })}>
          <span className="text-xs text-muted-foreground">{d.work.open ? pct(d.work.late / d.work.open) : "0%"} of open work</span>
        </Kpi>
        <Kpi label="Collected per $1 billed" value={d.billed.current ? `$${(d.collected.current / d.billed.current).toFixed(2)}` : "—"} hint="Payments received ÷ invoices issued in the period" />
      </Kpis>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Work in progress" description="Open engagements by service and stage. Red is past its due date.">
          {d.work.byService.length === 0 ? (
            <Empty>No open work.</Empty>
          ) : (
            <div className="space-y-5">
              {d.work.byService.map((w) => (
                <div key={w.module}>
                  <button type="button" onClick={() => open({ kind: "open_work", title: `Open ${w.label} engagements`, params: { module: w.module } })} className="mb-1 flex w-full items-baseline justify-between gap-2 px-2 text-left text-sm font-semibold hover:underline">
                    <span>{w.label}</span>
                    <span className="tabular text-xs font-normal text-muted-foreground">
                      {w.open} open{w.late ? <span className="text-red-700 dark:text-red-300"> · {w.late} late</span> : null}
                    </span>
                  </button>
                  {w.stages.map((st) => (
                    <ShareBar key={st.key} label={st.name} value={st.count} share={st.count / Math.max(...w.stages.map((x) => x.count))} tone={st.late ? "danger" : "primary"} sub={st.late ? `${st.late} past due` : undefined} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section title="The firm over time" description="Month-end figures from the daily snapshot ClientFlow saves.">
          {trend.length < 2 ? (
            <Empty>The trend builds up from here: ClientFlow saves these figures every day.</Empty>
          ) : (
            <TableScroll label="The firm over time table">
              <table className="w-full min-w-[360px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-2 font-medium">Month</th>
                    <th className="py-2 pr-2 text-right font-medium">Owed to you</th>
                    <th className="py-2 pr-2 text-right font-medium">Recurring / month</th>
                    <th className="py-2 text-right font-medium">Active clients</th>
                  </tr>
                </thead>
                <tbody>
                  {[...trend].reverse().map((t) => (
                    <tr key={t.month} className="border-b border-border/60 last:border-0">
                      <td className="py-1.5 pr-2">{monthLong(t.month)}</td>
                      <td className="tabular py-1.5 pr-2 text-right">{money(t.receivablesCents)}</td>
                      <td className="tabular py-1.5 pr-2 text-right">{money(t.recurringCents)}</td>
                      <td className="tabular py-1.5 text-right">{t.activeClients}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Section>
      </div>
    </>
  );
}

// --- Revenue ------------------------------------------------------------------------------------------

function RevenueView({ d, open }: { d: Revenue; open: (x: Drill) => void }) {
  const [series, setSeries] = useState<"billed" | "collected">("billed");
  const chart = d.months.map((m) => ({ label: monthLabel(m.month), month: m.month, thisYear: (series === "billed" ? m.billed : m.collected) / 100, lastYear: (series === "billed" ? m.billedLastYear : m.collectedLastYear) / 100 }));
  const totalServices = d.byService.reduce((t, x) => t + x.current, 0);
  const rec = d.recurringVsOneTime;
  const recShare = d.total.current ? rec.recurring.current / d.total.current : 0;
  const biggest = d.topClients[0];
  return (
    <>
      <Kpis>
        <Kpi label="Billed" value={money(d.total.current)} onOpen={() => open({ kind: "billed", title: "Invoices issued" })}>
          <Change current={d.total.current} previous={d.total.previous} count={d.byService.reduce((t, x) => t + x.count, 0)} prevCount={d.byService.reduce((t, x) => t + x.prevCount, 0)} format={money} />
        </Kpi>
        <Kpi label="Recurring share" value={pct(recShare)} hint="Billed for monthly accounting, payroll and CFO work">
          <span className="text-xs text-muted-foreground">{money(rec.recurring.current)} recurring · {money(rec.oneTime.current)} one-time</span>
        </Kpi>
        <Kpi label="Billed per client" value={money(d.perClient.current)}>
          <Change {...d.perClient} format={money} />
        </Kpi>
        <Kpi label="Recurring monthly revenue" value={money(rec.monthlyPlans.monthlyCents)} hint="The monthly fees of active plans today">
          <span className="text-xs text-muted-foreground">{money(rec.monthlyPlans.monthlyCents * 12)} a year at today's plans</span>
        </Kpi>
      </Kpis>

      <section className="mb-6 rounded-lg border border-card-border bg-card">
        <header className="flex flex-col gap-2 border-b border-border px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-[15px] font-semibold">By month</h2>
            <p className="text-xs text-muted-foreground">The last 12 months, each beside the same month a year earlier.</p>
          </div>
          <Segmented label="Series" value={series} onChange={setSeries} options={[{ value: "billed", label: "Billed" }, { value: "collected", label: "Collected" }]} />
        </header>
        <div className="px-3 py-4">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chart} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="var(--viz-grid)" vertical={false} />
              <XAxis dataKey="label" {...AXIS} interval="preserveStartEnd" minTickGap={8} />
              <YAxis {...AXIS} tickFormatter={compact} width={52} axisLine={false} />
              <Tooltip content={<ChartTip />} cursor={{ fill: "var(--viz-grid)", opacity: 0.5 }} />
              <Legend {...LEGEND} />
              <Bar dataKey="thisYear" name="This year" fill="var(--viz-1)" radius={[4, 4, 0, 0]} maxBarSize={28} />
              <Line dataKey="lastYear" name="Same month last year" stroke="var(--viz-2)" strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="By service" description="Click a service for its invoices.">
          {d.byService.length === 0 ? (
            <Empty>Nothing billed in this period.</Empty>
          ) : (
            <div className="space-y-1">
              {d.byService.map((x) => (
                <ShareBar
                  key={x.key}
                  label={x.label}
                  value={money(x.current)}
                  share={totalServices ? x.current / totalServices : 0}
                  tone={x.key === "unlinked" ? "muted" : "primary"}
                  onClick={() => open({ kind: "billed", title: `Billed: ${x.label}`, params: { service: x.key } })}
                  sub={<Change current={x.current} previous={x.previous} count={x.count} prevCount={x.prevCount} format={money} />}
                />
              ))}
              {d.byService.some((x) => x.key === "unlinked" && x.current > 0) && <Note>"Not linked to a service" is invoices with no engagement or service chosen. New invoices ask for one.</Note>}
            </div>
          )}
        </Section>
        <Section title="Tax returns by form" description="What each type of return brought in, and the average fee.">
          {d.byReturnType.length === 0 ? (
            <Empty>No invoices linked to tax returns in this period.</Empty>
          ) : (
            <TableScroll label="Tax returns by form table">
              <table className="w-full min-w-[380px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-2 font-medium">Form</th>
                    <th className="py-2 pr-2 text-right font-medium">Returns</th>
                    <th className="py-2 pr-2 text-right font-medium">Average fee</th>
                    <th className="py-2 text-right font-medium">Last year</th>
                  </tr>
                </thead>
                <tbody>
                  {d.byReturnType.map((r) => (
                    <tr key={r.returnType} className="border-b border-border/60 last:border-0">
                      <td className="py-2 pr-2">{r.label}</td>
                      <td className="tabular py-2 pr-2 text-right">{r.returns}</td>
                      <td className="tabular py-2 pr-2 text-right font-medium">{money(r.averageCents)}</td>
                      <td className="tabular py-2 text-right text-muted-foreground">{r.prevReturns ? `${money(r.prevAverageCents)} (${r.prevReturns})` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Section>
        <Section title="Top clients" description={`${d.clientsBilled.current} client${d.clientsBilled.current === 1 ? "" : "s"} billed in this period.`}>
          {d.topClients.length === 0 ? (
            <Empty>Nothing billed in this period.</Empty>
          ) : (
            <div className="space-y-1">
              {biggest && biggest.share > 0.15 && d.clientsBilled.current >= SMALL_NUMBER && (
                <Note tone="warning">
                  {biggest.name} is {pct(biggest.share)} of what you billed. One client above 15% is a risk if they leave.
                </Note>
              )}
              {d.topClients.map((c) => (
                <Link key={c.clientId} href={`/clients/${c.clientId}`} className="block rounded-md hover:bg-muted/60">
                  <ShareBar label={c.name} value={money(c.cents)} share={c.share} tone={c.share > 0.15 ? "warning" : "primary"} sub={`${pct(c.share, 1)} of billed · ${c.invoices} invoice${c.invoices === 1 ? "" : "s"}`} />
                </Link>
              ))}
            </div>
          )}
        </Section>
        <Section title="Quoted vs billed" description="Finished work that had a quoted fee: what was billed against the quote.">
          {d.quotedVsBilled.engagements === 0 ? (
            <Empty>No finished work with a quoted fee in this period. Enter a quoted fee on an engagement to compare.</Empty>
          ) : (
            <>
              <div className="mb-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="text-xs text-muted-foreground">Quoted</p>
                  <p className="tabular font-semibold">{money(d.quotedVsBilled.quotedCents)}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="text-xs text-muted-foreground">Billed</p>
                  <p className="tabular font-semibold">{money(d.quotedVsBilled.billedCents)}</p>
                </div>
                <div className={cn("rounded-md p-2", d.quotedVsBilled.under ? "bg-amber-50 dark:bg-amber-950/40" : "bg-muted/50")}>
                  <p className="text-xs text-muted-foreground">Billed under quote</p>
                  <p className="tabular font-semibold">{d.quotedVsBilled.under}</p>
                </div>
              </div>
              <PagedList items={d.quotedVsBilled.items} noun="engagements">
                {(page) => (
              <ul className="divide-y divide-border text-sm">
                {page.map((x) => (
                  <li key={x.engagementId}>
                    <Link href={`/engagements/${x.engagementId}`} className="flex items-center justify-between gap-3 py-2 hover:underline">
                      <span className="min-w-0 truncate">
                        {x.client} <span className="text-muted-foreground">· {x.name}</span>
                      </span>
                      <span className={cn("tabular shrink-0", x.billedCents < x.quotedCents ? "text-amber-700 dark:text-amber-300" : "")}>
                        {money(x.billedCents)} / {money(x.quotedCents)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
                )}
              </PagedList>
            </>
          )}
        </Section>
      </div>
    </>
  );
}

// --- Billing and collections ------------------------------------------------------------------------------

const METHOD_LABELS: Record<string, string> = { stripe: "Card or bank, online", check: "Check", cash: "Cash", zelle: "Zelle", bank_product: "From the refund (bank product)", other: "Other" };

function BillingView({ d, open }: { d: Billing; open: (x: Drill) => void }) {
  const owed = d.aging.reduce((t, b) => t + b.cents, 0);
  const colors: Record<string, string> = { current: "bg-emerald-500", "1_30": "bg-sky-500", "31_60": "bg-amber-400", "61_90": "bg-orange-500", "90_plus": "bg-red-600" };
  const rate = d.collectionRate;
  return (
    <>
      <Kpis>
        <Kpi label="Clients owe you" value={money(owed)} onOpen={() => open({ kind: "receivables", title: "Open invoices" })}>
          <span className="text-xs text-muted-foreground">{d.aging.reduce((t, b) => t + b.invoices, 0)} open invoices</span>
        </Kpi>
        <Kpi label="Average days to get paid" value={days(d.daysToPay.current)} hint="From the invoice date to the day it was paid in full">
          <Change current={d.daysToPay.current} previous={d.daysToPay.previous} count={d.daysToPay.count} prevCount={d.daysToPay.prevCount} format={(v) => days(v)} invert />
        </Kpi>
        <Kpi label="Collection rate" value={pct(rate.current)} hint="Collected ÷ billed in the period">
          <Change current={rate.current} previous={rate.previous} count={rate.billed.count} prevCount={rate.billed.prevCount} format={(v) => pct(v)} points />
        </Kpi>
        <Kpi label="Coming in next 30 days" value={money(d.dueSoon.next30.cents)} hint="Open invoices due in the next 30 days">
          <span className="text-xs text-muted-foreground">
            {money(d.dueSoon.next7.cents)} in the next 7 days · {d.dueSoon.next30.invoices} invoice{d.dueSoon.next30.invoices === 1 ? "" : "s"}
          </span>
        </Kpi>
      </Kpis>

      <Section title="What clients owe, by age" description="Days past the due date (or the invoice date when there's none). Click an age for its invoices." className="mb-6">
        {owed === 0 ? (
          <Empty>No unpaid invoices. Every issued invoice is paid.</Empty>
        ) : (
          <>
            <div className="mb-4 flex h-3 overflow-hidden rounded-full bg-muted" role="img" aria-label="Receivables by age">
              {d.aging.filter((b) => b.cents > 0).map((b) => (
                <div key={b.key} className={colors[b.key]} style={{ width: `${(b.cents / owed) * 100}%` }} title={`${b.label}: ${money(b.cents)}`} />
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {d.aging.map((b) => (
                <button key={b.key} type="button" disabled={!b.invoices} onClick={() => open({ kind: "receivables", title: `Open invoices: ${b.label.toLowerCase()}`, params: { bucket: b.key } })} className="flex flex-col items-start rounded-md border border-border p-3 text-left transition hover:border-primary/40 hover:bg-muted/40 disabled:opacity-60 disabled:hover:bg-transparent">
                  <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <span className={cn("h-2 w-2 rounded-full", colors[b.key])} aria-hidden />
                    {b.label}
                  </p>
                  <p className="tabular mt-1 text-lg font-semibold">{money(b.cents)}</p>
                  <p className="text-xs text-muted-foreground">
                    {b.clientCount ? `${b.clientCount} client${b.clientCount === 1 ? "" : "s"}` : "None"}
                    {b.clients[0] ? ` · ${b.clients[0].name}${b.clientCount > 1 ? "…" : ""}` : ""}
                  </p>
                </button>
              ))}
            </div>
          </>
        )}
      </Section>

      {d.fromRefunds && d.fromRefunds.awaitingApproval.invoices + d.fromRefunds.awaitingTransfer.invoices + d.fromRefunds.received.payments + d.fromRefunds.declined > 0 && (
        <Section title="Fees taken from refunds" description="The client approves the fee in their portal, which bills it; it's collected when the refund partner pays the firm." className="mb-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-border p-3">
              <p className="text-xs font-medium text-muted-foreground">Waiting for the client's approval</p>
              <p className="tabular mt-1 text-lg font-semibold">{money(d.fromRefunds.awaitingApproval.cents)}</p>
              <p className="text-xs text-muted-foreground">
                {d.fromRefunds.awaitingApproval.invoices} fee{d.fromRefunds.awaitingApproval.invoices === 1 ? "" : "s"}, not billed yet
                {d.fromRefunds.declined ? ` · ${d.fromRefunds.declined} sent back by the client` : ""}
              </p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-xs font-medium text-muted-foreground">Approved, waiting for the refund</p>
              <p className="tabular mt-1 text-lg font-semibold">{money(d.fromRefunds.awaitingTransfer.cents)}</p>
              <p className="text-xs text-muted-foreground">
                {d.fromRefunds.awaitingTransfer.invoices} invoice{d.fromRefunds.awaitingTransfer.invoices === 1 ? "" : "s"}, billed and not yet collected
              </p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-xs font-medium text-muted-foreground">Received from refunds in this period</p>
              <p className="tabular mt-1 text-lg font-semibold">{money(d.fromRefunds.received.cents)}</p>
              <p className="text-xs text-muted-foreground">
                {d.fromRefunds.received.payments} payment{d.fromRefunds.received.payments === 1 ? "" : "s"} from the refund partner
              </p>
            </div>
          </div>
        </Section>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Work not yet billed" description="Finished tax returns, and other finished work with a quoted fee, that have no invoice (the last 12 months).">
          {d.unbilled.count === 0 ? (
            <Empty>Every finished engagement has been invoiced.</Empty>
          ) : (
            <>
              <p className="mb-3 text-sm">
                <span className="font-semibold">{d.unbilled.count}</span> engagement{d.unbilled.count === 1 ? "" : "s"}
                {d.unbilled.quotedCents ? (
                  <>
                    , <span className="font-semibold">{money(d.unbilled.quotedCents)}</span> quoted
                  </>
                ) : null}
                {d.unbilled.withoutQuote ? <span className="text-muted-foreground"> ({d.unbilled.withoutQuote} without a quoted fee)</span> : null}
              </p>
              <ul className="divide-y divide-border text-sm">
                {d.unbilled.items.slice(0, 10).map((x) => (
                  <li key={x.engagementId}>
                    <Link href={`/engagements/${x.engagementId}`} className="flex items-center justify-between gap-3 py-2 hover:underline">
                      <span className="min-w-0 truncate">
                        {x.client} <span className="text-muted-foreground">· {x.name}</span>
                      </span>
                      <span className="tabular shrink-0 text-muted-foreground">{x.quotedCents !== null ? money(x.quotedCents) : formatDate(x.completedOn)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              {d.unbilled.count > 10 && (
                <button type="button" className="mt-2 text-sm text-primary hover:underline" onClick={() => open({ kind: "unbilled", title: "Work not yet billed" })}>
                  See all {d.unbilled.count}
                </button>
              )}
            </>
          )}
        </Section>
        <Section title="How clients pay" description="Payments received in the period, and how long each way took from the invoice date.">
          {d.methods.length === 0 ? (
            <Empty>No payments in this period.</Empty>
          ) : (
            <div className="space-y-1">
              {d.methods.map((m) => (
                <ShareBar
                  key={m.method}
                  label={METHOD_LABELS[m.method] ?? m.method}
                  value={money(m.cents)}
                  share={m.cents / Math.max(...d.methods.map((x) => x.cents))}
                  onClick={() => open({ kind: "collected", title: `Payments: ${METHOD_LABELS[m.method] ?? m.method}`, params: { method: m.method } })}
                  sub={`${m.payments} payment${m.payments === 1 ? "" : "s"} · ${m.averageDays === null ? "—" : `${days(m.averageDays)} on average`}`}
                />
              ))}
            </div>
          )}
          <div className="mt-4 rounded-md border border-border bg-muted/30 p-3 text-sm">
            <p className="font-medium">Documents held until paid</p>
            <p className="text-muted-foreground">
              {d.held.documents
                ? `${d.held.documents} document${d.held.documents === 1 ? "" : "s"} waiting on ${d.held.invoices} invoice${d.held.invoices === 1 ? "" : "s"} (${money(d.held.cents)}).`
                : "No documents are being held for payment."}
            </p>
          </div>
        </Section>
      </div>
    </>
  );
}

// --- Staff and capacity -------------------------------------------------------------------------------------

function StaffView({ d, open }: { d: Staff; open: (x: Drill) => void }) {
  const r = d.clientResponse;
  const totals = d.people.reduce((t, p) => ({ open: t.open + p.open.total, late: t.late + p.open.late, due: t.due + p.open.dueNext14 }), { open: 0, late: 0, due: 0 });
  return (
    <>
      <Kpis>
        <Kpi label="Open work" value={totals.open} onOpen={() => open({ kind: "open_work", title: "Open engagements" })}>
          <span className="text-xs text-muted-foreground">Across {d.people.length} people</span>
        </Kpi>
        <Kpi label="Past due" value={totals.late} tone={totals.late ? "danger" : undefined} onOpen={() => open({ kind: "late_work", title: "Engagements past their due date" })}>
          <span className="text-xs text-muted-foreground">{totals.due} returns due in the next 14 days</span>
        </Kpi>
        <Kpi label="Typical time for clients to respond" value={days(r.current.medianDays)}>
          <Change current={r.current.medianDays} previous={r.previous.medianDays} count={r.current.requests} prevCount={r.previous.requests} format={(v) => days(v)} invert />
        </Kpi>
        <Kpi label="Reminders per request" value={r.current.averageReminders === null ? "—" : r.current.averageReminders.toFixed(1)}>
          <span className="text-xs text-muted-foreground">
            {r.current.requests} request{r.current.requests === 1 ? "" : "s"} answered
          </span>
        </Kpi>
      </Kpis>
      <Section
        title="The team"
        description="Workload and timeliness for each person, with the returns they finished, so harder work is visible. There's no single score or ranking."
      >
        {!d.trackerOn && (
          <div className="mb-4">
            <Note>
              Hours and utilization appear when the time tracker is on.{" "}
              {d.owner ? (
                <Link href="/settings?tab=time" className="font-medium text-primary hover:underline">
                  Switch it on in Firm Settings › Team › Team and access › Time & costs
                </Link>
              ) : (
                "The owner can switch it on in Firm Settings."
              )}
            </Note>
          </div>
        )}
        <TeamTable people={d.people} trackerOn={d.trackerOn} showsCost={d.showsCost} />
      </Section>
      <Section title="Time at each stage" className="mt-6" description="Average days work spent at each stage, for engagements finished in the period. The longest stage is where work waits.">
        {d.stageTimes.length === 0 ? (
          <Empty>No engagements finished in this period.</Empty>
        ) : (
          // One service under another: a nine-stage list beside a three-stage one didn't line up.
          <div className="space-y-6">
            {d.stageTimes.map((m) => {
              const max = Math.max(...m.stages.map((x) => x.averageDays), 0.01);
              return (
                <div key={m.module}>
                  <p className="mb-1 px-2 text-sm font-semibold">{m.label}</p>
                  {m.stages.map((st, i) => (
                    <ShareBar key={st.key} label={st.name} value={days(st.averageDays)} share={st.averageDays / max} tone={i === 0 && m.stages.length > 1 ? "warning" : "primary"} sub={`${st.engagements} engagement${st.engagements === 1 ? "" : "s"}`} />
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </>
  );
}

/**
 * The team side by side: every figure in its own column, so they line up however many people there are.
 * Two tables (workload, then billing and time) so every column fits at 1280 px with nothing hidden behind
 * an inner scroll; on a phone, one stacked block per person with the same labels.
 */
function TeamTable({ people, trackerOn, showsCost }: { people: Person[]; trackerOn: boolean; showsCost: boolean }) {
  const share = (n: number, of: number) => (of === 0 ? "—" : of < SMALL_NUMBER ? `${n} of ${of}` : pct(n / of));
  const th = "px-3 py-2.5 text-right align-bottom font-medium first:pl-0 first:text-left last:pr-0";
  const td = "px-3 py-3 text-right align-top tabular first:pl-0 first:text-left last:pr-0";
  const services = (p: Person) => (p.open.byService.length ? p.open.byService.map((x) => `${x.label} ${x.count}`).join(" · ") : "No open work");
  const returns = (p: Person) => (p.returnsCompleted.length > 0 ? `Returns finished: ${p.returnsCompleted.map((r) => `${r.returnType === "1120S" ? "1120-S" : (r.returnType ?? "other")} ×${r.count}`).join(" · ")}` : null);
  const hours = (p: Person) => (p.time ? formatMinutes(p.time.minutes) : "—");
  const utilization = (p: Person) => (p.time && p.time.minutes > 0 && p.time.utilization !== null ? `${pct(p.time.utilization)} of a ${p.time.weeklyHours ?? 40} h week` : null);
  const cost = (p: Person) => (p.time?.costCents === null || p.time?.costCents === undefined ? null : money(p.time.costCents));
  const lateCls = (p: Person) => (p.open.late ? "font-semibold text-red-700 dark:text-red-300" : "text-muted-foreground");
  const review = (p: Person) => (p.review ? `${days(p.review.medianDays)} typical` : null);
  return (
    <>
      <div className="hidden space-y-6 md:block">
        <TableScroll label="Workload and timeliness table">
          <p className="mb-1 text-sm font-semibold">Workload and timeliness</p>
          <table className="w-full text-sm">
            <caption className="sr-only">Workload and timeliness</caption>
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className={cn(th, "w-[30%]")}>Person</th>
                <th className={th}>Open work</th>
                <th className={th}>Past due</th>
                <th className={th}>Due in 14 days</th>
                <th className={th}>Finished on time</th>
                <th className={th}>Tasks on time</th>
                <th className={th}>Reviews signed off</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {people.map((p) => (
                <tr key={p.userId}>
                  <td className={td}>
                    <p className="font-medium">{p.name}</p>
                    <p className="mt-0.5 text-xs font-normal text-muted-foreground">{services(p)}</p>
                    {returns(p) && <p className="mt-0.5 text-xs text-muted-foreground">{returns(p)}</p>}
                  </td>
                  <td className={cn(td, "font-semibold")}>{p.open.total}</td>
                  <td className={cn(td, lateCls(p))}>{p.open.late}</td>
                  <td className={td}>{p.open.dueNext14}</td>
                  <td className={td}>{share(p.onTime.engagementsOnTime, p.onTime.engagements)}</td>
                  <td className={cn(td, "whitespace-nowrap")}>{share(p.onTime.tasksOnTime, p.onTime.tasks)}</td>
                  <td className={td}>
                    {p.review ? p.review.signedOff : 0}
                    {review(p) && <p className="whitespace-nowrap text-xs text-muted-foreground">{review(p)}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        <TableScroll label="Billing and time table">
          <p className="mb-1 text-sm font-semibold">Billing and time</p>
          <table className="w-full text-sm">
            <caption className="sr-only">Billing and time</caption>
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className={cn(th, "w-[30%]")}>Person</th>
                <th className={th}>Billed on their work</th>
                {trackerOn && <th className={th}>Hours logged</th>}
                {trackerOn && showsCost && <th className={th}>Cost</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {people.map((p) => (
                <tr key={p.userId}>
                  <td className={cn(td, "font-medium")}>{p.name}</td>
                  <td className={cn(td, "font-medium")}>{money(p.billedCents)}</td>
                  {trackerOn && (
                    <td className={td}>
                      {hours(p)}
                      {utilization(p) && <p className="whitespace-nowrap text-xs text-muted-foreground">{utilization(p)}</p>}
                    </td>
                  )}
                  {trackerOn && showsCost && <td className={td}>{cost(p) ?? <span className="text-xs text-muted-foreground">No rate</span>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      </div>
      <ul className="divide-y divide-border md:hidden">
        {people.map((p) => {
          const rows: Array<[string, ReactNode, string?]> = [
            ["Open work", p.open.total, "font-semibold"],
            ["Past due", p.open.late, lateCls(p)],
            ["Due in 14 days", p.open.dueNext14],
            ["Finished on time", share(p.onTime.engagementsOnTime, p.onTime.engagements)],
            ["Tasks on time", share(p.onTime.tasksOnTime, p.onTime.tasks)],
            ["Reviews signed off", `${p.review ? p.review.signedOff : 0}${review(p) ? ` · ${review(p)}` : ""}`],
            ["Billed on their work", money(p.billedCents), "font-medium"],
            ...(trackerOn ? [["Hours logged", `${hours(p)}${utilization(p) ? ` · ${utilization(p)}` : ""}`] as [string, ReactNode]] : []),
            ...(trackerOn && showsCost ? [["Cost", cost(p) ?? "No rate"] as [string, ReactNode]] : []),
          ];
          return (
            <li key={p.userId} className="py-3 first:pt-0 last:pb-0">
              <p className="font-medium">{p.name}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{services(p)}</p>
              {returns(p) && <p className="mt-0.5 text-xs text-muted-foreground">{returns(p)}</p>}
              <dl className="mt-2 space-y-1 text-sm">
                {rows.map(([label, value, cls]) => (
                  <div key={label} className="flex items-baseline justify-between gap-3">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className={cn("tabular text-right", cls)}>{value}</dd>
                  </div>
                ))}
              </dl>
            </li>
          );
        })}
      </ul>
    </>
  );
}

// --- Clients ----------------------------------------------------------------------------------------------

function ClientsView({ d, open }: { d: Clients; open: (x: Drill) => void }) {
  const profitPage = usePaged(d.profitability?.items ?? []);
  const riskPage = usePaged(d.atRisk.items);
  const t = d.returningTaxClients;
  const totalSvc = Math.max(...d.byService.map((x) => x.clients), 1);
  return (
    <>
      <Kpis>
        <Kpi label="Active clients" value={d.active}>
          <span className="text-xs text-muted-foreground">{d.multiService} use two or more services</span>
        </Kpi>
        <Kpi label="New clients" value={d.gained.current} onOpen={() => open({ kind: "gained", title: "New clients" })}>
          <Change current={d.gained.current} previous={d.gained.previous} count={d.gained.current} prevCount={d.gained.previous} />
        </Kpi>
        <Kpi label="Clients lost" value={d.lost.current} tone={d.lost.current > 0 ? "warning" : undefined} onOpen={() => open({ kind: "lost", title: "Clients lost" })}>
          <Change current={d.lost.current} previous={d.lost.previous} count={d.lost.current} prevCount={d.lost.previous} invert />
        </Kpi>
        <Kpi label="Kept through the period" value={pct(d.retention.current, 1)} hint="Clients at the start of the period who were still clients at its end">
          <Change current={d.retention.current} previous={d.retention.previous} count={d.retention.count} prevCount={d.retention.prevCount} format={(v) => pct(v, 1)} points />
        </Kpi>
      </Kpis>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Tax clients who came back" description="Clients with last year's return who also have this year's.">
          <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
            {[t.current, t.previous].map((x, i) => (
              <div key={x.taxYear} className={cn("rounded-md border border-border p-3")}>
                <p className="text-xs text-muted-foreground">
                  {x.taxYear} returns{i === 0 ? " (this season)" : ""}
                </p>
                <p className="tabular text-xl font-semibold">{x.lastYear ? (x.lastYear < SMALL_NUMBER ? `${x.back} of ${x.lastYear}` : pct(x.back / x.lastYear)) : "—"}</p>
                <p className="text-xs text-muted-foreground">{x.lastYear ? `${x.back} of ${x.lastYear} from ${x.taxYear - 1}` : `No ${x.taxYear - 1} returns to compare`}</p>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Clients by service">
          <div className="space-y-1">
            {d.byService.map((s) => (
              <ShareBar key={s.module} label={s.label} value={s.clients} share={s.clients / totalSvc} />
            ))}
          </div>
        </Section>
        <Section title="Could use another service" description="Clients whose services suggest a next one. A starting point for a conversation, not a sales list.">
          {d.opportunities.length === 0 ? (
            <Empty>No obvious openings right now.</Empty>
          ) : (
            <ul className="space-y-4">
              {d.opportunities.map((o) => (
                <li key={o.offer}>
                  <p className="flex items-baseline justify-between gap-2 text-sm font-medium">
                    <span>{o.label}</span>
                    <span className="tabular text-muted-foreground">{o.count}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Offer {o.service}:{" "}
                    {o.clients.map((c, i) => (
                      <span key={c.clientId}>
                        {i > 0 && ", "}
                        <Link href={`/clients/${c.clientId}`} className="text-primary hover:underline">
                          {c.name}
                        </Link>
                      </span>
                    ))}
                    {o.count > o.clients.length && ` and ${o.count - o.clients.length} more`}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title="Clients at risk" description="Late payments, requests ignored after reminders, or no portal sign-in for 90 days.">
          {d.atRisk.count === 0 ? (
            <Empty>No client shows warning signs.</Empty>
          ) : (
            <>
            <ul className="divide-y divide-border text-sm">
              {riskPage.items.map((c) => (
                <li key={c.clientId}>
                  {/* On a phone the reasons go under the name, so a long list isn't cut off at the edge. */}
                  <Link href={`/clients/${c.clientId}`} className="flex flex-col gap-0.5 py-2 hover:underline sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                    <span className="min-w-0 truncate font-medium">{c.name}</span>
                    <span className="text-xs text-muted-foreground sm:max-w-[60%] sm:shrink-0 sm:text-right">{c.reasons.join(" · ")}</span>
                  </Link>
                </li>
              ))}
              {d.atRisk.count > d.atRisk.items.length && <li className="pt-2 text-xs text-muted-foreground">and {d.atRisk.count - d.atRisk.items.length} more</li>}
            </ul>
              <Pager paged={riskPage} noun="clients" className="mt-3" compact />
            </>
          )}
        </Section>
      </div>
        <Section className="mt-6" title="How fast new clients get going" description={`Typical days for the ${d.onboarding.clients} client${d.onboarding.clients === 1 ? "" : "s"} who started in this period.`}>
          <ol className="grid gap-3 sm:grid-cols-3">
            {[
              ["Inquiry to client", d.onboarding.leadToClientDays],
              ["Client to first signature", d.onboarding.clientToSignedDays],
              ["Client to first invoice", d.onboarding.clientToInvoiceDays],
            ].map(([label, v], i) => (
              <li key={String(label)} className="rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">
                  Step {i + 1}: {label}
                </p>
                <p className="tabular text-lg font-semibold">{days(v as number | null)}</p>
              </li>
            ))}
          </ol>
        </Section>
        {d.profitability && (
          <Section className="mt-6" title="Profit by client (owner only)" description="Billed in the period against the cost of the hours logged on each client.">
            {d.profitability.items.length === 0 ? (
              <Empty>No time logged in this period.</Empty>
            ) : (
              <>
                {d.profitability.missingRates > 0 && <Note tone="warning">Some hours belong to people with no cost rate yet. Set them in Firm Settings › Team › Team and access › Time & costs.</Note>}
                <TableScroll label="Profit by client table" className="mt-2">
                  <table className="w-full min-w-[420px] text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-2 font-medium">Client</th>
                        <th className="py-2 pr-2 text-right font-medium">Hours</th>
                        <th className="py-2 pr-2 text-right font-medium">Billed</th>
                        <th className="py-2 pr-2 text-right font-medium">Cost</th>
                        <th className="py-2 text-right font-medium">Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitPage.items.map((c) => {
                        const margin = c.costCents === null || !c.billedCents ? null : (c.billedCents - c.costCents) / c.billedCents;
                        return (
                          <tr key={c.clientId} className="border-b border-border/60 last:border-0">
                            <td className="max-w-[12rem] truncate py-2 pr-2">
                              <Link href={`/clients/${c.clientId}`} className="hover:underline">
                                {c.name}
                              </Link>
                            </td>
                            <td className="tabular py-2 pr-2 text-right">{formatMinutes(c.minutes)}</td>
                            <td className="tabular py-2 pr-2 text-right">{money(c.billedCents)}</td>
                            <td className="tabular py-2 pr-2 text-right">{c.costCents === null ? "—" : money(c.costCents)}</td>
                            <td className={cn("tabular py-2 text-right font-medium", margin !== null && margin < 0.2 ? "text-red-700 dark:text-red-300" : "")}>{margin === null ? "—" : pct(margin)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </TableScroll>
                <Pager paged={profitPage} noun="clients" className="mt-3" />
              </>
            )}
          </Section>
        )}
    </>
  );
}

// --- Growth -----------------------------------------------------------------------------------------------

function GrowthView({ d }: { d: Growth }) {
  const l = d.leads;
  const m = d.meetings;
  const conv = l.current ? l.won / l.current : null;
  const prevConv = l.previous ? l.prevWon / l.previous : null;
  const maxLeads = Math.max(...l.sources.map((x) => x.leads), 1);
  return (
    <>
      <Kpis>
        <Kpi label="New leads" value={l.current}>
          <Change current={l.current} previous={l.previous} count={l.current} prevCount={l.previous} />
        </Kpi>
        <Kpi label="Became clients" value={l.won}>
          <Change current={l.won} previous={l.prevWon} count={l.won} prevCount={l.prevWon} />
        </Kpi>
        <Kpi label="Conversion rate" value={pct(conv)}>
          <Change current={conv} previous={prevConv} count={l.current} prevCount={l.previous} format={(v) => pct(v)} points />
        </Kpi>
        <Kpi label="Consultations that became clients" value={m.current.consultations ? `${m.current.consultationsWon} of ${m.current.consultations}` : "—"} />
      </Kpis>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Leads by source" description="Where inquiries came from, how many became clients, how fast someone replied, and what those clients have been billed since.">
          {l.sources.length === 0 ? (
            <Empty>No leads in this period.</Empty>
          ) : (
            <div className="space-y-1">
              {l.sources.map((s) => (
                <ShareBar
                  key={s.source}
                  label={s.label}
                  value={`${s.leads} lead${s.leads === 1 ? "" : "s"}`}
                  share={s.leads / maxLeads}
                  sub={
                    <span>
                      {s.won} became {s.won === 1 ? "a client" : "clients"}{s.leads ? ` (${pct(s.won / s.leads)})` : ""} · {s.medianHoursToContact === null ? "no first contact recorded" : `first reply ${s.medianHoursToContact < 1 ? "within an hour" : s.medianHoursToContact < 24 ? `in ${Math.round(s.medianHoursToContact)} h` : `in ${days(s.medianHoursToContact / 24)}`}`}
                      {s.revenueCents ? ` · ${money(s.revenueCents)} billed` : ""} · last year {s.prevLeads}
                    </span>
                  }
                />
              ))}
            </div>
          )}
        </Section>
        <div className="flex flex-col gap-6">
        <Section title="Meetings" description="Meetings that were scheduled in the period (cancelled ones aren't counted as booked).">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(
              [
                ["Booked", m.current.booked, m.previous.booked],
                ["Held", m.current.held, m.previous.held],
                ["No-shows", m.current.noShows, m.previous.noShows],
                ["Cancelled", m.current.cancelled, m.previous.cancelled],
              ] as const
            ).map(([label, cur, prev]) => (
              <div key={label} className="rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="tabular text-lg font-semibold">{cur}</p>
                <p className="text-xs text-muted-foreground">last year {prev}</p>
              </div>
            ))}
          </div>
          {m.current.booked > 0 && <p className="mt-3 text-sm text-muted-foreground">No-show rate: {pct(m.current.noShows / m.current.booked)}</p>}
        </Section>
        <Section className="flex-1" title="Time the AI saved (estimate)" description={`An estimate: ${d.ai.assumptions.read} minutes for each document read and confirmed, ${d.ai.assumptions.proposal} minutes for each AI suggestion approved.`}>
          <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-3">
            <div className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">About</p>
              <p className="tabular text-lg font-semibold">{formatMinutes(d.ai.current.minutesSaved)}</p>
              <p className="text-xs text-muted-foreground">last year {formatMinutes(d.ai.previous.minutesSaved)}</p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">Documents read</p>
              <p className="tabular text-lg font-semibold">{d.ai.current.documentsRead}</p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">Suggestions approved</p>
              <p className="tabular text-lg font-semibold">{d.ai.current.proposalsApproved}</p>
            </div>
          </div>
        </Section>
        </div>
      </div>
    </>
  );
}


